import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { employeeAPI, esiAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

const monthLabel = (month) => {
  const n = Number(month);
  const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return labels[n - 1] || String(month);
};

const employeeDisplayName = (row) => {
  const id = row.employee_id || row.employeeId;
  const full = [row.first_name, row.middle_name, row.last_name].filter(Boolean).join(' ').trim();
  return full ? `${full} (${id})` : String(id || '');
};

const EsiModule = ({ section = 'policy' }) => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [policy, setPolicy] = useState(null);

  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const [policyForm, setPolicyForm] = useState({
    effective_from: todayISO,
    effective_to: '',
    wage_ceiling: '',
    employee_rate_percent: '',
    employer_rate_percent: '',
    rounding_precision: '',
  });

  const [branchForm, setBranchForm] = useState({
    branch_code: '',
    branch_name: '',
    state: '',
    district: '',
    address: '',
    is_active: true,
  });
  const [branchEdit, setBranchEdit] = useState(null);
  const [showBranchDialog, setShowBranchDialog] = useState(false);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({
    branch_office_id: '',
    insurance_number: '',
    tic_number: '',
    ip_registration_status: 'not_registered',
    is_covered: false,
    coverage_start: '',
    coverage_end: '',
    exemption_reason: '',
  });

  const [period, setPeriod] = useState(() => {
    const now = new Date();
    return { year: String(now.getFullYear()), month: String(now.getMonth() + 1) };
  });

  const [contribs, setContribs] = useState([]);
  const [challans, setChallans] = useState([]);

  const loadBranches = async () => {
    try {
      const res = await esiAPI.listBranches({ include_inactive: true });
      setBranches(res.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load ESI branches'));
    }
  };

  const loadPolicy = async () => {
    try {
      const res = await esiAPI.getPolicy({});
      setPolicy(res.data || null);
      if (res.data) {
        setPolicyForm((prev) => ({
          ...prev,
          effective_from: res.data.effective_from || prev.effective_from,
          effective_to: res.data.effective_to || '',
          wage_ceiling: res.data.wage_ceiling != null ? String(res.data.wage_ceiling) : prev.wage_ceiling,
          employee_rate_percent:
            res.data.employee_rate_percent != null ? String(res.data.employee_rate_percent) : prev.employee_rate_percent,
          employer_rate_percent:
            res.data.employer_rate_percent != null ? String(res.data.employer_rate_percent) : prev.employer_rate_percent,
          rounding_precision:
            res.data.rounding_precision != null ? String(res.data.rounding_precision) : prev.rounding_precision,
        }));
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load ESI policy'));
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await employeeAPI.list();
      setEmployees(res.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load employees'));
    }
  };

  const loadContributions = async () => {
    try {
      const params = { year: Number(period.year), month: Number(period.month) };
      const res = await esiAPI.listContributions(params);
      setContribs(res.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load contributions'));
    }
  };

  const loadChallans = async () => {
    try {
      const params = { year: Number(period.year), month: Number(period.month) };
      const res = await esiAPI.listChallans(params);
      setChallans(res.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load challans'));
    }
  };

  useEffect(() => {
    loadBranches();
    loadPolicy();
    loadEmployees();
  }, []);

  useEffect(() => {
    if (section === 'contributions') loadContributions();
    if (section === 'challans') loadChallans();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only on section change; loaders are stable
  }, [section]);

  const branchLabelById = useMemo(() => {
    const map = new Map();
    branches.forEach((b) => map.set(String(b.id), `${b.branch_name} (${b.branch_code})`));
    return map;
  }, [branches]);

  const openCreateBranch = () => {
    setBranchEdit(null);
    setBranchForm({ branch_code: '', branch_name: '', state: '', district: '', address: '', is_active: true });
    setShowBranchDialog(true);
  };

  const openEditBranch = (row) => {
    setBranchEdit(row);
    setBranchForm({
      branch_code: row.branch_code || '',
      branch_name: row.branch_name || '',
      state: row.state || '',
      district: row.district || '',
      address: row.address || '',
      is_active: Boolean(row.is_active),
    });
    setShowBranchDialog(true);
  };

  const submitBranch = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...branchForm };
      if (branchEdit?.id) {
        await esiAPI.updateBranch(branchEdit.id, payload);
        toast.success('Branch updated');
      } else {
        await esiAPI.createBranch(payload);
        toast.success('Branch created');
      }
      setShowBranchDialog(false);
      await loadBranches();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save branch'));
    }
  };

  const submitPolicyUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        effective_from: policyForm.effective_from,
        effective_to: policyForm.effective_to || null,
        wage_ceiling: Number(policyForm.wage_ceiling),
        employee_rate_percent: Number(policyForm.employee_rate_percent),
        employer_rate_percent: Number(policyForm.employer_rate_percent),
        rounding_precision: Number(policyForm.rounding_precision),
        is_active: true,
        config: {},
      };
      if (policy?.id) {
        await esiAPI.updatePolicy(policy.id, payload);
        toast.success('Policy updated');
      } else {
        await esiAPI.createPolicy(payload);
        toast.success('Policy created');
      }
      await loadPolicy();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save policy'));
    } finally {
      setLoading(false);
    }
  };

  const submitPolicyCreateNew = async () => {
    try {
      setLoading(true);
      const payload = {
        effective_from: policyForm.effective_from,
        effective_to: policyForm.effective_to || null,
        wage_ceiling: Number(policyForm.wage_ceiling),
        employee_rate_percent: Number(policyForm.employee_rate_percent),
        employer_rate_percent: Number(policyForm.employer_rate_percent),
        rounding_precision: Number(policyForm.rounding_precision),
        is_active: true,
        config: {},
      };
      await esiAPI.createPolicy(payload);
      toast.success('Policy created');
      await loadPolicy();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create policy'));
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeProfile = async (employeeId) => {
    if (!employeeId) return;
    try {
      const res = await esiAPI.getEmployeeProfile(employeeId);
      setEmployeeProfile(res.data || null);
      const row = res.data || {};
      setProfileForm({
        branch_office_id: row.branch_office_id != null ? String(row.branch_office_id) : '',
        insurance_number: row.insurance_number || '',
        tic_number: row.tic_number || '',
        ip_registration_status: row.ip_registration_status || 'not_registered',
        is_covered: Boolean(row.is_covered),
        coverage_start: row.coverage_start || '',
        coverage_end: row.coverage_end || '',
        exemption_reason: row.exemption_reason || '',
      });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load ESI profile'));
    }
  };

  const submitEmployeeProfile = async (e) => {
    e.preventDefault();
    if (!selectedEmployeeId) return;
    try {
      setLoading(true);
      const payload = {
        branch_office_id: profileForm.branch_office_id ? Number(profileForm.branch_office_id) : null,
        insurance_number: profileForm.insurance_number || null,
        tic_number: profileForm.tic_number || null,
        ip_registration_status: profileForm.ip_registration_status || 'not_registered',
        is_covered: Boolean(profileForm.is_covered),
        coverage_start: profileForm.coverage_start || null,
        coverage_end: profileForm.coverage_end || null,
        exemption_reason: profileForm.exemption_reason || null,
        nominee: {},
        family: [],
        metadata: {},
        is_active: true,
      };
      const res = await esiAPI.upsertEmployeeProfile(selectedEmployeeId, payload);
      setEmployeeProfile(res.data || null);
      toast.success('ESI profile saved');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save ESI profile'));
    } finally {
      setLoading(false);
    }
  };

  const generateContributions = async () => {
    try {
      setLoading(true);
      const params = { year: Number(period.year), month: Number(period.month) };
      const res = await esiAPI.generateContributions(params);
      toast.success(`Generated contributions (${res.data?.created || 0} created, ${res.data?.updated || 0} updated)`);
      await loadContributions();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate contributions'));
    } finally {
      setLoading(false);
    }
  };

  const generateChallan = async () => {
    try {
      setLoading(true);
      const params = { year: Number(period.year), month: Number(period.month) };
      await esiAPI.generateChallan(params);
      toast.success('Challan generated');
      await loadChallans();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate challan'));
    } finally {
      setLoading(false);
    }
  };

  const header = section === 'policy' ? 'ESI Policy' : section === 'branches' ? 'ESI Branches' : section === 'employees' ? 'ESI Employees' : section === 'contributions' ? 'ESI Contributions' : 'ESI Challans';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle>{header}</CardTitle>
            <div className="flex items-center gap-2">
              {(section === 'policy' || section === 'branches' || section === 'employees') && (
                <Button variant="outline" onClick={() => { loadPolicy(); loadBranches(); }} disabled={loading}>
                  Refresh
                </Button>
              )}
              {section === 'branches' && <Button onClick={openCreateBranch}>New Branch</Button>}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {section === 'policy' && (
            <form onSubmit={submitPolicyUpdate} className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Effective From</Label>
                <Input type="date" value={policyForm.effective_from} onChange={(e) => setPolicyForm((p) => ({ ...p, effective_from: e.target.value }))} />
              </div>
              <div>
                <Label>Effective To</Label>
                <Input type="date" value={policyForm.effective_to} onChange={(e) => setPolicyForm((p) => ({ ...p, effective_to: e.target.value }))} />
              </div>
              <div>
                <Label>Wage Ceiling</Label>
                <Input type="number" value={policyForm.wage_ceiling} onChange={(e) => setPolicyForm((p) => ({ ...p, wage_ceiling: e.target.value }))} />
              </div>
              <div>
                <Label>Employee Rate (%)</Label>
                <Input type="number" step="0.01" value={policyForm.employee_rate_percent} onChange={(e) => setPolicyForm((p) => ({ ...p, employee_rate_percent: e.target.value }))} />
              </div>
              <div>
                <Label>Employer Rate (%)</Label>
                <Input type="number" step="0.01" value={policyForm.employer_rate_percent} onChange={(e) => setPolicyForm((p) => ({ ...p, employer_rate_percent: e.target.value }))} />
              </div>
              <div>
                <Label>Rounding Precision</Label>
                <Input type="number" value={policyForm.rounding_precision} onChange={(e) => setPolicyForm((p) => ({ ...p, rounding_precision: e.target.value }))} />
              </div>
              <div className="md:col-span-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{policy?.id ? `Policy ID: ${policy.id}` : 'Not configured'}</Badge>
                  {policy?.wage_ceiling != null && <Badge variant="outline">Ceiling: {policy.wage_ceiling}</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" onClick={submitPolicyCreateNew} disabled={loading}>
                    Create New
                  </Button>
                  <Button type="submit" disabled={loading}>{policy?.id ? 'Save' : 'Create'}</Button>
                </div>
              </div>
            </form>
          )}

          {section === 'branches' && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>District</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {branches.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground">No branches</TableCell>
                  </TableRow>
                )}
                {branches.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.branch_code}</TableCell>
                    <TableCell>{row.branch_name}</TableCell>
                    <TableCell>{row.state || '-'}</TableCell>
                    <TableCell>{row.district || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={row.is_active ? 'success' : 'secondary'}>{row.is_active ? 'Active' : 'Inactive'}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => openEditBranch(row)}>Edit</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {section === 'employees' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <Label>Employee</Label>
                  <Select
                    value={selectedEmployeeId}
                    onValueChange={(value) => {
                      setSelectedEmployeeId(value);
                      loadEmployeeProfile(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((row) => {
                        const id = row.employee_id || row.employeeId;
                        if (!id) return null;
                        return (
                          <SelectItem key={String(id)} value={String(id)}>
                            {employeeDisplayName(row)}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={() => loadEmployeeProfile(selectedEmployeeId)} disabled={!selectedEmployeeId || loading}>
                    Reload Profile
                  </Button>
                </div>
              </div>

              {employeeProfile && (
                <form onSubmit={submitEmployeeProfile} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-1">
                    <Label>Branch Office</Label>
                    <Select
                      value={profileForm.branch_office_id ? profileForm.branch_office_id : '__unassigned__'}
                      onValueChange={(v) =>
                        setProfileForm((p) => ({ ...p, branch_office_id: v === '__unassigned__' ? '' : v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select branch" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unassigned__">Unassigned</SelectItem>
                        {branches.filter((b) => b.is_active).map((b) => (
                          <SelectItem key={String(b.id)} value={String(b.id)}>
                            {branchLabelById.get(String(b.id)) || String(b.id)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Insurance Number</Label>
                    <Input value={profileForm.insurance_number} onChange={(e) => setProfileForm((p) => ({ ...p, insurance_number: e.target.value }))} />
                  </div>
                  <div>
                    <Label>TIC Number</Label>
                    <Input value={profileForm.tic_number} onChange={(e) => setProfileForm((p) => ({ ...p, tic_number: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Registration Status</Label>
                    <Select value={profileForm.ip_registration_status} onValueChange={(v) => setProfileForm((p) => ({ ...p, ip_registration_status: v }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_registered">Not registered</SelectItem>
                        <SelectItem value="tic_issued">TIC issued</SelectItem>
                        <SelectItem value="registered">Registered</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="space-y-1">
                      <Label>Covered</Label>
                      <div className="text-muted-foreground text-xs">ESI coverage flag</div>
                    </div>
                    <Switch checked={profileForm.is_covered} onCheckedChange={(v) => setProfileForm((p) => ({ ...p, is_covered: Boolean(v) }))} />
                  </div>
                  <div />
                  <div>
                    <Label>Coverage Start</Label>
                    <Input type="date" value={profileForm.coverage_start} onChange={(e) => setProfileForm((p) => ({ ...p, coverage_start: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Coverage End</Label>
                    <Input type="date" value={profileForm.coverage_end} onChange={(e) => setProfileForm((p) => ({ ...p, coverage_end: e.target.value }))} />
                  </div>
                  <div />
                  <div className="md:col-span-3">
                    <Label>Exemption Reason</Label>
                    <Textarea value={profileForm.exemption_reason} onChange={(e) => setProfileForm((p) => ({ ...p, exemption_reason: e.target.value }))} />
                  </div>
                  <div className="md:col-span-3 flex justify-end">
                    <Button type="submit" disabled={loading}>Save</Button>
                  </div>
                </form>
              )}
            </div>
          )}

          {(section === 'contributions' || section === 'challans') && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <Label>Year</Label>
                <Input type="number" value={period.year} onChange={(e) => setPeriod((p) => ({ ...p, year: e.target.value }))} />
              </div>
              <div>
                <Label>Month</Label>
                <Select value={period.month} onValueChange={(v) => setPeriod((p) => ({ ...p, month: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }).map((_, idx) => {
                      const value = String(idx + 1);
                      return (
                        <SelectItem key={value} value={value}>
                          {monthLabel(value)}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { section === 'contributions' ? loadContributions() : loadChallans(); }} disabled={loading}>
                  Refresh
                </Button>
                {section === 'contributions' && (
                  <Button onClick={generateContributions} disabled={loading}>Generate</Button>
                )}
                {section === 'challans' && (
                  <Button onClick={generateChallan} disabled={loading}>Generate</Button>
                )}
              </div>
              {section === 'contributions' && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      const url = esiAPI.exportContributionRegisterCsvUrl({ year: period.year, month: period.month });
                      window.open(url, '_blank');
                    }}
                  >
                    Export CSV
                  </Button>
                </div>
              )}
            </div>
          )}

          {section === 'contributions' && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead className="text-right">Wage</TableHead>
                  <TableHead className="text-right">Emp</TableHead>
                  <TableHead className="text-right">Empr</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contribs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-muted-foreground">No contributions</TableCell>
                  </TableRow>
                )}
                {contribs.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.employee_id}</TableCell>
                    <TableCell>{row.branch_office_id ? (branchLabelById.get(String(row.branch_office_id)) || row.branch_office_id) : '-'}</TableCell>
                    <TableCell className="text-right">{Number(row.wage_contributable || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">{Number(row.employee_contribution || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">{Number(row.employer_contribution || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">{Number(row.total_contribution || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={row.eligibility_status === 'covered' ? 'success' : 'secondary'}>{row.eligibility_status}</Badge>
                    </TableCell>
                    <TableCell>{row.generation_source}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {section === 'challans' && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Branch</TableHead>
                  <TableHead className="text-right">Emp Total</TableHead>
                  <TableHead className="text-right">Empr Total</TableHead>
                  <TableHead className="text-right">Grand Total</TableHead>
                  <TableHead className="text-right">Employees</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {challans.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground">No challans</TableCell>
                  </TableRow>
                )}
                {challans.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.branch_office_id ? (branchLabelById.get(String(row.branch_office_id)) || row.branch_office_id) : 'All branches'}</TableCell>
                    <TableCell className="text-right">{Number(row.employee_total || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">{Number(row.employer_total || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">{Number(row.grand_total || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">{row.employee_count}</TableCell>
                    <TableCell>
                      <Badge variant={row.status === 'paid' ? 'success' : 'secondary'}>{row.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showBranchDialog} onOpenChange={setShowBranchDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{branchEdit?.id ? 'Edit ESI Branch' : 'Create ESI Branch'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitBranch} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Branch Code</Label>
              <Input value={branchForm.branch_code} onChange={(e) => setBranchForm((p) => ({ ...p, branch_code: e.target.value }))} required />
            </div>
            <div>
              <Label>Branch Name</Label>
              <Input value={branchForm.branch_name} onChange={(e) => setBranchForm((p) => ({ ...p, branch_name: e.target.value }))} required />
            </div>
            <div>
              <Label>State</Label>
              <Input value={branchForm.state} onChange={(e) => setBranchForm((p) => ({ ...p, state: e.target.value }))} />
            </div>
            <div>
              <Label>District</Label>
              <Input value={branchForm.district} onChange={(e) => setBranchForm((p) => ({ ...p, district: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <Textarea value={branchForm.address} onChange={(e) => setBranchForm((p) => ({ ...p, address: e.target.value }))} />
            </div>
            <div className="md:col-span-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch checked={branchForm.is_active} onCheckedChange={(v) => setBranchForm((p) => ({ ...p, is_active: Boolean(v) }))} />
                <span className="text-sm text-muted-foreground">Active</span>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setShowBranchDialog(false)}>Cancel</Button>
                <Button type="submit">{branchEdit?.id ? 'Save' : 'Create'}</Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EsiModule;
